import { useEffect, useMemo, useState } from 'react';

import { Alert, Box, Button, Group, LoadingOverlay, ScrollArea, Table, Text } from '@mantine/core';

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { scudMappingApi } from '@/shared/api/scudMapping';
import { SCUD_EMPTY, SCUD_MAX_LIMIT, SCUD_PAGE_SIZES } from '@/shared/constant/scudMapping';
import { useShowNotifaction } from '@/shared/hooks/useShowNotifaction';
import {
	IScudMappingPermissions,
	IScudMappingReason,
	IScudPersonExclusion,
	TScudExclusionListQuery,
} from '@/shared/types/scudMapping.types';
import { LabelInput, Select } from '@/shared/uikit/components/input';

import { IconPlus } from '@tabler/icons-react';

import { ScudExclusionForm } from './ScudExclusionForm';
import {
	CellLines,
	DeleteIconButton,
	EditIconButton,
	ScudTable,
	ScudTablePager,
	ScudTh,
	pageCount,
	useTableSort,
} from './ScudTable';
import { SortableTh } from './SortableTh';
import { SCUD_EXCLUSIONS_KEY, SCUD_REASONS_ALL_KEY } from './queryKeys';
import { formatScudDateTime, reasonLabelById, requestErrorText } from './utils';

type TExclusionSort = 'full_name' | 'location' | 'reason' | 'comment' | 'created' | 'updated';

type TExclusionFilter = Pick<TScudExclusionListQuery, 'location' | 'search'>;

/** API исключений не сортирует по колонкам: забираем весь отбор и сортируем на клиенте */
const fetchAllExclusions = async (filter: TExclusionFilter) => {
	const objects: IScudPersonExclusion[] = [];
	let total = 0;

	do {
		const page = await scudMappingApi.getExclusions({
			...filter,
			offset: objects.length,
			limit: SCUD_MAX_LIMIT,
		});

		objects.push(...page.objects);
		total = page.total_amount;

		if (!page.objects.length) {
			break;
		}
	} while (objects.length < total);

	return objects;
};

const fullName = (exclusion: IScudPersonExclusion) =>
	exclusion.full_name_federation ?? `id ${exclusion.person_id_federation}`;

const sortValue = (
	exclusion: IScudPersonExclusion,
	field: TExclusionSort,
	reasons: IScudMappingReason[],
) => {
	switch (field) {
		case 'full_name':
			return fullName(exclusion);
		case 'location':
			return exclusion.location;
		case 'reason':
			return reasonLabelById(reasons, exclusion.reason_id, exclusion.reason_name);
		case 'comment':
			return exclusion.comment?.trim() || null;
		case 'created':
			return exclusion.created_at;
		case 'updated':
			return exclusion.updated_at ?? null;
	}
};

/** Пустые значения всегда в конце, как в списке пропусков; при равенстве — порядок сервера: ФИО, локация */
const sortExclusions = (
	exclusions: IScudPersonExclusion[],
	field: TExclusionSort,
	desc: boolean,
	reasons: IScudMappingReason[],
) =>
	[...exclusions].sort((a, b) => {
		const left = sortValue(a, field, reasons);
		const right = sortValue(b, field, reasons);

		if (left && right) {
			const result = left.localeCompare(right, 'ru');

			if (result) {
				return desc ? -result : result;
			}
		} else if (left || right) {
			return left ? -1 : 1;
		}

		return (
			fullName(a).localeCompare(fullName(b), 'ru') || a.location.localeCompare(b.location, 'ru')
		);
	});

type ExclusionRowProps = {
	exclusion: IScudPersonExclusion;
	number: number;
	reasonLabel: string | null;
	onEdit: (exclusion: IScudPersonExclusion) => void;
	onDelete: (id: number) => void;
};

const ExclusionRow = ({ exclusion, number, reasonLabel, onEdit, onDelete }: ExclusionRowProps) => (
	<Table.Tr>
		<Table.Td>
			<Text size="sm" c="dimmed">
				{number}
			</Text>
		</Table.Td>
		<Table.Td>
			<CellLines main={exclusion.full_name_federation ?? `id ${exclusion.person_id_federation}`} />
		</Table.Td>
		<Table.Td>
			<Text size="sm">{exclusion.location}</Text>
		</Table.Td>
		<Table.Td>
			<Text size="sm">{reasonLabel ?? SCUD_EMPTY}</Text>
		</Table.Td>
		<Table.Td maw={320}>
			<Text size="sm" lineClamp={3}>
				{exclusion.comment ?? SCUD_EMPTY}
			</Text>
		</Table.Td>
		<Table.Td style={{ whiteSpace: 'nowrap' }}>
			<CellLines main={exclusion.created_by} sub={formatScudDateTime(exclusion.created_at)} />
		</Table.Td>
		<Table.Td style={{ whiteSpace: 'nowrap' }}>
			{exclusion.updated_by ? (
				<CellLines main={exclusion.updated_by} sub={formatScudDateTime(exclusion.updated_at)} />
			) : (
				<Text size="sm">{SCUD_EMPTY}</Text>
			)}
		</Table.Td>
		<Table.Td style={{ width: '1%', whiteSpace: 'nowrap' }}>
			{exclusion.can_process && (
				<Group gap={2} wrap="nowrap" justify="flex-end">
					<EditIconButton onClick={() => onEdit(exclusion)} />
					<DeleteIconButton confirmText="Удалить исключение?" onOk={() => onDelete(exclusion.id)} />
				</Group>
			)}
		</Table.Td>
	</Table.Tr>
);

type ScudExclusionsProps = {
	permissions: IScudMappingPermissions;
};

/** Вкладка «Исключения» (ТЗ 9): кто не попадает в отчёт «Бронировали, но нет пропуска» */
export const ScudExclusions = ({ permissions }: ScudExclusionsProps) => {
	const queryClient = useQueryClient();
	const [location, setLocation] = useState<string | null>(null);
	const [searchDraft, setSearchDraft] = useState('');
	const [search, setSearch] = useState('');
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(Number(SCUD_PAGE_SIZES[0]));
	const [editing, setEditing] = useState<IScudPersonExclusion | null>(null);
	const [isFormOpened, setIsFormOpened] = useState(false);

	const processLocations = permissions.locations.filter((item) => item.can_process);

	const filter: TExclusionFilter = {
		location: location ?? undefined,
		search: search.trim() || undefined,
	};

	const { data, isLoading, isError, error } = useQuery({
		queryKey: [...SCUD_EXCLUSIONS_KEY, filter],
		queryFn: () => fetchAllExclusions(filter),
		placeholderData: keepPreviousData,
	});

	const { data: reasons = [] } = useQuery({
		queryKey: SCUD_REASONS_ALL_KEY,
		queryFn: () => scudMappingApi.getReasons(false),
		staleTime: 300_000,
	});

	const { sort, desc, onSort } = useTableSort<TExclusionSort>('full_name', () => setPage(1));
	const thProps = { sort, desc, onSort };

	const sorted = useMemo(
		() => (isError ? [] : sortExclusions(data ?? [], sort, desc, reasons)),
		[data, isError, sort, desc, reasons],
	);
	const total = sorted.length;
	const pages = pageCount(total, pageSize);
	const exclusions = sorted.slice((page - 1) * pageSize, page * pageSize);

	useEffect(() => {
		if (data && page > pages) {
			setPage(pages);
		}
	}, [data, page, pages]);

	const { mutate: remove } = useMutation({
		mutationFn: scudMappingApi.deleteExclusion,
		onSuccess: () => useShowNotifaction({ text: 'Исключение удалено', type: 'success' }),
		onError: (error) =>
			useShowNotifaction({
				text: requestErrorText(error, 'Не удалось удалить исключение'),
				type: 'error',
			}),
		onSettled: () => void queryClient.invalidateQueries({ queryKey: SCUD_EXCLUSIONS_KEY }),
	});

	const openForm = (exclusion: IScudPersonExclusion | null) => {
		setEditing(exclusion);
		setIsFormOpened(true);
	};

	const applySearch = () => {
		setSearch(searchDraft);
		setPage(1);
	};

	return (
		<Box
			pos="relative"
			style={{ display: 'flex', flexDirection: 'column', gap: 12, overflow: 'hidden', flex: 1 }}
		>
			<LoadingOverlay visible={isLoading} zIndex={100} overlayProps={{ radius: 2, blur: 2 }} />
			<Group justify="space-between" align="flex-end">
				<Group align="flex-end" gap="sm">
					<Select
						compact
						caption="Локация"
						placeholder={`Все доступные (${permissions.locations.length})`}
						w={240}
						data={permissions.locations.map((item) => item.location)}
						value={location}
						onChange={(value) => {
							setLocation(value);
							setPage(1);
						}}
						clearable
					/>
					<LabelInput
						compact
						label="Сотрудник"
						placeholder="ФИО"
						w={300}
						value={searchDraft}
						onChange={(event) => setSearchDraft(event.currentTarget.value)}
						clearable
						onClear={() => {
							setSearchDraft('');
							setSearch('');
							setPage(1);
						}}
						onKeyDown={(event) => {
							if (event.key === 'Enter') {
								applySearch();
							}
						}}
					/>
					<Button h={42} variant="light" onClick={applySearch}>
						Найти
					</Button>
				</Group>
				{processLocations.length > 0 && (
					<Button h={42} leftSection={<IconPlus size={16} />} onClick={() => openForm(null)}>
						Добавить
					</Button>
				)}
			</Group>

			{isError ? (
				<Alert color="red" variant="light">
					{requestErrorText(error, 'Не удалось загрузить исключения')}
				</Alert>
			) : (
				<Text size="sm" c="dimmed">
					Найдено: {total}
				</Text>
			)}

			<ScrollArea style={{ flex: 1 }} scrollbarSize={12} offsetScrollbars>
				<ScudTable miw={1100}>
					<Table.Thead>
						<Table.Tr>
							<ScudTh width={48}>№</ScudTh>
							<SortableTh {...thProps} field="full_name">
								Сотрудник в «Федерации»
							</SortableTh>
							<SortableTh {...thProps} field="location">
								Локация
							</SortableTh>
							<SortableTh {...thProps} field="reason">
								Причина
							</SortableTh>
							<SortableTh {...thProps} field="comment">
								Комментарий
							</SortableTh>
							<SortableTh {...thProps} field="created">
								Создано
							</SortableTh>
							<SortableTh {...thProps} field="updated">
								Изменено
							</SortableTh>
							<ScudTh>Действия</ScudTh>
						</Table.Tr>
					</Table.Thead>
					<Table.Tbody>
						{exclusions.map((exclusion, index) => (
							<ExclusionRow
								key={exclusion.id}
								exclusion={exclusion}
								number={(page - 1) * pageSize + index + 1}
								reasonLabel={reasonLabelById(reasons, exclusion.reason_id, exclusion.reason_name)}
								onEdit={openForm}
								onDelete={remove}
							/>
						))}
					</Table.Tbody>
				</ScudTable>
				{!isLoading && !isError && !exclusions.length && (
					<Text c="dimmed" ta="center" py="xl">
						Исключений нет
					</Text>
				)}
			</ScrollArea>

			<ScudTablePager
				page={page}
				pageSize={pageSize}
				total={total}
				onPageChange={setPage}
				onPageSizeChange={(value) => {
					setPageSize(value);
					setPage(1);
				}}
			/>

			<ScudExclusionForm
				opened={isFormOpened}
				exclusion={editing}
				onClose={() => setIsFormOpened(false)}
				locations={processLocations}
				reasons={reasons}
			/>
		</Box>
	);
};
